import React, { useState } from 'react'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import {
  useAnalysisTypeRealEstates,
  useAnalysisType
} from 'redux/hooks/AnalysisTypes'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import SearchAndSelectPopover from 'components_new/molecules/SearchAndSelectPopover'

// Up to this many chips show; past it the rest fold into "+n till".
const MAX_CHIPS = 6

interface AnalysisTypeExclusionsProps {
  context: 'dialog' | 'page'
  id: string
}

/**
 * The properties left out of every run of the analysis type, edited in
 * place.
 */
const AnalysisTypeExclusions = (props: AnalysisTypeExclusionsProps) => {
  const { context, id } = props

  const { analysisType } = useAnalysisType(id)
  const realEstates = useAnalysisTypeRealEstates()
  const { tryUpdateAnalysisTypeSettings } = useAnalysisTypeActions()
  const { t } = useTranslation()

  // Each save sends the whole list, so one waits for the one before.
  const [saving, setSaving] = useState(false)

  if (!analysisType) return null

  const excluded = analysisType.excluded_real_estates
  const shown =
    excluded.length > MAX_CHIPS ? excluded.slice(0, MAX_CHIPS - 1) : excluded
  const hidden = excluded.length - shown.length

  // An excluded name that has left the register stays last, so it can be
  // removed.
  const options = [
    ...realEstates.realEstates,
    ...excluded.filter((name) => !realEstates.realEstates.includes(name))
  ].map((name) => ({ id: name, title: name }))

  const save = (names: string[]) => {
    setSaving(true)
    tryUpdateAnalysisTypeSettings(id, { excluded_real_estates: names }, () =>
      setSaving(false)
    )
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        py: 1
      }}
    >
      {shown.map((name) => (
        <Chip
          disabled={saving}
          key={name}
          label={name}
          onDelete={() => save(excluded.filter((item) => item !== name))}
          size="small"
        />
      ))}
      <SearchAndSelectPopover
        disabled={saving}
        loading={realEstates.loading}
        onFocus={realEstates.fetch}
        onSubmit={save}
        options={options}
        placeholder={t('analysis_types', 'excluded_search_placeholder')}
        popoverContent={
          <Text color="text.secondary" variant="body2">
            {context === 'dialog'
              ? t('analysis_types', 'excluded_picker_intro_dialog')
              : t('analysis_types', 'excluded_picker_intro_page')}
          </Text>
        }
        popoverWidth={360}
        tooltip={t('analysis_types', 'excluded_edit')}
        value={excluded}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
          {hidden > 0 ? (
            <Chip
              clickable={true}
              color="black"
              label={t('analysis_types', 'excluded_more', false, {
                count: String(hidden)
              })}
              size="small"
            />
          ) : null}
          <IconButton
            size="small"
            sx={{
              border: '1px dashed',
              borderColor: 'text.disabled',
              height: 26,
              width: 26
            }}
          >
            <Icon fontSize="small" name="Add" />
          </IconButton>
        </Box>
      </SearchAndSelectPopover>
    </Box>
  )
}

export default AnalysisTypeExclusions
